import React, { createContext, useContext, useState } from "react";

import {
  getIndividualFlexSavings,
  getIndividualFlexSavingsStats,
  getIndividualGoalSavings,
  getIndividualGoalSavingsStats,
} from "api/individual-savings";
import {
  IIndividualFlexSavingsPayload,
  IIndividualFlexSavingsResult,
  IIndividualFlexSavingsStats,
  IIndividualFlexSavingsStatsData,
  IIndividualFlexSavingsStatsPayload,
  IIndividualGoalSavingsPayload,
  IIndividualGoalSavingsResult,
  IIndividualGoalSavingsStats,
  IIndividualGoalSavingsStatsData,
  IIndividualGoalSavingsStatsPayload,
} from "api/individual-savings/types";
import { START_INDEX } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";

import {
  IIndividualsSavingsContextProps,
  IIndividualsSavingsProviderProps,
} from "./types";

export const DEFAULT_INDIVIDUALS_FLEX_SAVINGS_STATS: IIndividualFlexSavingsStats =
  {
    totalFlexSavingsAmount: 0,
    totalWithdrawalAmount: 0,
    totalInterestEarned: 0,
    numberOfActiveFlexSavings: 0,
    totalNumberOfFlexSavers: 0,
  };

export const DEFAULT_INDIVIDUALS_GOAL_SAVINGS_STATS: IIndividualGoalSavingsStats =
  {
    totalAmountSaved: 0,
    totalInterestEarned: 0,
    totalWithdrawal: 0,
    totalActiveGoals: 0,
    saversCount: 0,
  };

const IndividualsSavingsContext =
  createContext<IIndividualsSavingsContextProps>({
    individualsTableHeaderCTAWidth: START_INDEX,
    handleUpdateIndividualsTableHeaderCTAWidth: () => {
      return null;
    },
    isLoadingIndividualFlexSavingsStats: false,
    setIsLoadingIndividualFlexSavingsStats: () => {
      return null;
    },
    individualFlexSavingsStats: DEFAULT_INDIVIDUALS_FLEX_SAVINGS_STATS,
    setIndividualFlexSavingsStats: () => {
      return null;
    },
    handleGetIndividualFlexSavingsStats: async (
      _: IIndividualFlexSavingsStatsPayload
    ) => {
      // do nothing
    },
    isLoadingIndividualFlexSavings: false,
    setIsLoadingIndividualFlexSavings: () => {
      return null;
    },
    individualFlexSavingsResult: null,
    setIndividualFlexSavingsResult: () => {
      return null;
    },
    handleGetIndividualFlexSavings: async (
      _: IIndividualFlexSavingsPayload
    ) => {
      // do nothing
    },
    isLoadingIndividualGoalSavingsStats: false,
    setIsLoadingIndividualGoalSavingsStats: () => {
      return null;
    },
    individualGoalSavingsStats: DEFAULT_INDIVIDUALS_GOAL_SAVINGS_STATS,
    setIndividualGoalSavingsStats: () => {
      return null;
    },
    handleGetIndividualGoalSavingsStats: async (
      _: IIndividualGoalSavingsStatsPayload
    ) => {
      // do nothing
    },
    isLoadingIndividualGoalSavings: false,
    setIsLoadingIndividualGoalSavings: () => {
      return null;
    },
    individualGoalSavingsResult: null,
    setIndividualGoalSavingsResult: () => {
      return null;
    },
    handleGetIndividualGoalSavings: async (
      _: IIndividualGoalSavingsPayload
    ) => {
      // do nothing
    },
  });

export const IndividualsSavingsProvider = (
  props: IIndividualsSavingsProviderProps
) => {
  const { children } = props;

  const [individualsTableHeaderCTAWidth, setIndividualsTableHeaderCTAWidth] =
    useState<number>(START_INDEX);
  const [
    isLoadingIndividualFlexSavingsStats,
    setIsLoadingIndividualFlexSavingsStats,
  ] = useState(false);
  const [individualFlexSavingsStats, setIndividualFlexSavingsStats] = useState<
    Omit<IIndividualFlexSavingsStatsData, "numberOfActiveFlexSavings">
  >(DEFAULT_INDIVIDUALS_FLEX_SAVINGS_STATS);
  const [isLoadingIndividualFlexSavings, setIsLoadingIndividualFlexSavings] =
    useState(false);
  const [individualFlexSavingsResult, setIndividualFlexSavingsResult] =
    useState<IIndividualFlexSavingsResult | null>(null);
  const [
    isLoadingIndividualGoalSavingsStats,
    setIsLoadingIndividualGoalSavingsStats,
  ] = useState(false);
  const [individualGoalSavingsStats, setIndividualGoalSavingsStats] = useState<
    Omit<IIndividualGoalSavingsStatsData, "numberOfActiveGoalSavings">
  >(DEFAULT_INDIVIDUALS_GOAL_SAVINGS_STATS);
  const [isLoadingIndividualGoalSavings, setIsLoadingIndividualGoalSavings] =
    useState(false);
  const [individualGoalSavingsResult, setIndividualGoalSavingsResult] =
    useState<IIndividualGoalSavingsResult | null>(null);

  const handleUpdateIndividualsTableHeaderCTAWidth = (value: number) => {
    setIndividualsTableHeaderCTAWidth(value);
  };

  const handleGetIndividualFlexSavingsStats = async (
    payload: IIndividualFlexSavingsStatsPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    const isRefetch = others?.isRefetching;
    try {
      if (!isRefetch) {
        setIsLoadingIndividualFlexSavingsStats(true);
      }
      const { error, message, data } =
        await getIndividualFlexSavingsStats(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualFlexSavingsStats(data);
      }
      setIsLoadingIndividualFlexSavingsStats(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetch) {
      setIsLoadingIndividualFlexSavingsStats(false);
    }
  };

  const handleGetIndividualFlexSavings = async (
    payload: IIndividualFlexSavingsPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    // const { isRefetching } = others;
    const refetch = others?.isRefetching;
    try {
      if (!refetch) {
        setIsLoadingIndividualFlexSavings(true);
      }
      const { error, message, data } = await getIndividualFlexSavings(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualFlexSavingsResult(data);
      }
      setIsLoadingIndividualFlexSavings(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!refetch) {
      setIsLoadingIndividualFlexSavings(false);
    }
  };
  const handleGetIndividualGoalSavingsStats = async (
    payload: IIndividualGoalSavingsStatsPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    const isRefetch = others?.isRefetching;
    try {
      if (!isRefetch) {
        setIsLoadingIndividualGoalSavingsStats(true);
      }
      const { error, message, data } =
        await getIndividualGoalSavingsStats(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualGoalSavingsStats(data);
      }
      setIsLoadingIndividualGoalSavingsStats(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetch) {
      setIsLoadingIndividualGoalSavingsStats(false);
    }
  };

  const handleGetIndividualGoalSavings = async (
    payload: IIndividualGoalSavingsPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    // const { isRefetching } = others;
    const refetch = others?.isRefetching;
    try {
      if (!refetch) {
        setIsLoadingIndividualGoalSavings(true);
      }
      const { error, message, data } = await getIndividualGoalSavings(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualGoalSavingsResult(data);
      }
      setIsLoadingIndividualGoalSavings(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!refetch) {
      setIsLoadingIndividualGoalSavings(false);
    }
  };

  return (
    <IndividualsSavingsContext.Provider
      value={{
        individualsTableHeaderCTAWidth,
        handleUpdateIndividualsTableHeaderCTAWidth,
        handleGetIndividualFlexSavings,
        isLoadingIndividualFlexSavings,
        setIsLoadingIndividualFlexSavings,
        handleGetIndividualFlexSavingsStats,
        isLoadingIndividualFlexSavingsStats,
        setIsLoadingIndividualFlexSavingsStats,
        individualFlexSavingsStats,
        setIndividualFlexSavingsStats,
        individualFlexSavingsResult,
        setIndividualFlexSavingsResult,
        handleGetIndividualGoalSavings,
        isLoadingIndividualGoalSavings,
        setIsLoadingIndividualGoalSavings,
        handleGetIndividualGoalSavingsStats,
        isLoadingIndividualGoalSavingsStats,
        setIsLoadingIndividualGoalSavingsStats,
        individualGoalSavingsStats,
        setIndividualGoalSavingsStats,
        individualGoalSavingsResult,
        setIndividualGoalSavingsResult,
        // isLoadingIndividualVaultSavings,
        // setIsLoadingIndividualVaultSavings,
        // handleGetIndividualVaultSavingsStats,
        // isLoadingIndividualVaultSavingsStats,
        // setIsLoadingIndividualVaultSavingsStats,
        // individualVaultSavingsStats,
        // setIndividualVaultSavingsStats,
        // individualVaultSavingsResult,
        // setIndividualVaultSavingsResult,
      }}
    >
      {children}
    </IndividualsSavingsContext.Provider>
  );
};

export const useIndividualsSavings = () => {
  const context = useContext(IndividualsSavingsContext);
  if (context === undefined) {
    throw new Error(
      "useIndividualsSavings must be used within a IndividualsSavingsProvider"
    );
  }
  return context;
};
